import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { generateTotpSecret, totpUri, totpCode, type Bytes } from '@battini/crypto';
import { api } from '../api/client.js';
import { useApp } from '../store/AppContext.js';
import { Alert, CopyButton } from '../components/ui.js';

export function MfaSetup() {
  const { security, reloadSecurity } = useApp();
  const navigate = useNavigate();
  const [secret, setSecret] = useState<{ base32: string; raw: Bytes } | null>(null);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const enabled = security?.mfaEnabled;

  useEffect(() => {
    if (!enabled && !secret) {
      const s = generateTotpSecret();
      setSecret(s);
    }
  }, [enabled, secret]);

  const uri = secret ? totpUri('you@battini.app', secret.base32) : '';

  const verify = async () => {
    if (!secret) return;
    setBusy(true);
    setMsg(null);
    try {
      // Validate locally first so a wrong code doesn't burn a request.
      const valid = await (async () => { try { return (await totpCode(secret.raw)) === code; } catch { return false; } })();
      if (!valid) throw new Error('That code doesn\'t match. Try the latest one from your app.');
      await api.post('/auth/mfa/setup', { secret: secret.base32, code });
      await reloadSecurity();
      setMsg('✓ Two-factor authentication enabled.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Setup failed');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api.post('/auth/mfa/disable', { code });
      await reloadSecurity();
      setMsg('MFA disabled.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  if (enabled) {
    return (
      <div className="panel" style={{ maxWidth: 560 }}>
        <h3 style={{ marginTop: 0 }}>Disable two-factor authentication</h3>
        <Alert kind="warn">Disabling MFA reduces protection against credential theft. Enter your current code to confirm.</Alert>
        {msg && <Alert kind="info">{msg}</Alert>}
        <div className="field"><label>Current code</label><input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} /></div>
        <div className="flex gap-sm">
          <button className="btn danger" onClick={disable} disabled={busy}>Disable MFA</button>
          <Link className="btn ghost" to="/settings">Back</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="panel" style={{ maxWidth: 560 }}>
      <h3 style={{ marginTop: 0 }}>Set up two-factor authentication</h3>
      <Alert kind="info">
        Add this account to your authenticator app (Authy, 1Password, Google Authenticator…).
        For security, the secret is generated in your browser and never sent to a QR service.
      </Alert>
      {secret && (
        <>
          <div className="field">
            <label>Secret (enter manually if needed)</label>
            <div className="flex gap-sm">
              <input className="mono" readOnly value={secret.base32} />
              <CopyButton value={secret.base32} />
            </div>
          </div>
          <div className="field">
            <label>Provisioning URI</label>
            <div className="mono" style={{ fontSize: 11, wordBreak: 'break-all', background: 'var(--bg-2)', padding: 8, borderRadius: 8 }}>{uri}</div>
          </div>
          <div className="field">
            <label>Enter the 6-digit code from your app</label>
            <input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
          </div>
          {msg && <Alert kind={msg.startsWith('✓') ? 'success' : 'error'}>{msg}</Alert>}
          <div className="flex gap-sm">
            <button className="btn" onClick={verify} disabled={busy || code.length !== 6}>Verify & enable</button>
            <button className="btn ghost" onClick={() => navigate('/settings')}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}
